import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import ArrayStore from 'devextreme/data/array_store';
import DataSource from 'devextreme/data/data_source';
import { Firma, ApiClientService, Sms_potraznja } from '../../../../services/api-client.service';
import { CacheService } from '../../../../services/cache.service';

@Component({
  selector: 'app-create-mobilna-potraznja',
  templateUrl: './create-mobilna-potraznja.component.html',
  styleUrls: ['./create-mobilna-potraznja.component.scss']
})
export class CreateMobilnaPotraznjaComponent {
  @Input() set visible(value: boolean) {
    this.isVisible = value;
    if (value) {
      this.initializeForm();
      this.loadLookupData();
    }
  }

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() smsPotraznjaCreated = new EventEmitter<Sms_potraznja>();

  isVisible = false;
  loading = false;
  dataLoading = false;
  smsPotraznjaForm!: FormGroup;
  maxDate = new Date();

  // Lookup data
  firme: Firma[] = [];
  drzave: any[] = [
    { id: 'Bosna i Hercegovina', name: 'Bosna i Hercegovina' },
    { id: 'Hrvatska', name: 'Hrvatska' },
    { id: 'Ino', name: 'Ino' },
    { id: 'Srbija', name: 'Srbija' }
  ];

  // Editor Options
  firmaEditorOptions: any = {
    dataSource: [],
    displayExpr: 'naziv',
    valueExpr: 'id',
    searchEnabled: true,
    placeholder: 'Odaberite firmu...',
    disabled: true
  };

  drzavaEditorOptions: any = {
    dataSource: this.drzave,
    displayExpr: 'name',
    valueExpr: 'id',
    placeholder: 'Odaberite državu...',
    onValueChanged: (e: any) => {
      if (e.value) {
        const valuta = this.generateValutaFromDrzava(e.value);
        this.smsPotraznjaForm.patchValue({
          valuta: valuta
        });
      }
    }
  };

  tekstEditorOptions: any = {
    placeholder: 'Unesite tekst SMS potražnje...',
    autoResizeEnabled: true,
    height: 120
  };

  valutaEditorOptions: any = {
    readOnly: true,
    disabled: true,
    placeholder: 'Automatski odabrano...'
  };

  constructor(
    private fb: FormBuilder, 
    private apiClient: ApiClientService, 
    private cache: CacheService
  ) {}

  onPopupShown() {
    this.initializeForm();
  }

  initializeForm() {
    this.smsPotraznjaForm = this.fb.group({
      id_firme: [null, Validators.required],
      drzava: [null, Validators.required],
      tekst: ['', Validators.required],
      valuta: [1], // Default to BIH
      datum: [new Date().toISOString().split('T')[0]]
    });
  }

  async loadLookupData() {
    this.dataLoading = true;

    try {
      const firmeData = await this.cache.getCachedOrFetch('firme', () => this.apiClient.firmeAll(10000, 0));
      this.firme = firmeData;

      this.updateEditorOptions();

    } catch (error) {
      console.error('Error loading lookup data:', error);
    } finally {
      this.dataLoading = false;
    }
  }

  private updateEditorOptions() {
    this.firmaEditorOptions = {
      ...this.firmaEditorOptions,
      dataSource: new DataSource({
        store: new ArrayStore({
          data: this.firme,
          key: 'id',
        }),
        paginate: true,
        pageSize: 50,
      }),
      disabled: false
    };
  }

  // Convert .NET logic to TypeScript
  private generateValutaFromDrzava(drzava: string): number {
    switch (drzava) {
      case "Bosna i Hercegovina":
        return 1; // BIH
      case "Hrvatska":
        return 3; // HRV
      case "Ino":
        return 2; // INO
      case "Srbija":
        return 4; // SRB
      default:
        return 1; // Default to BIH
    }
  }

  onFieldDataChanged(e: any) {
    if (e.dataField && e.value !== undefined) {
      this.smsPotraznjaForm.patchValue({
        [e.dataField]: e.value
      });

      // If drzava field changed, update valuta automatically
      if (e.dataField === 'drzava' && e.value) {
        const valuta = this.generateValutaFromDrzava(e.value);
        this.smsPotraznjaForm.patchValue({
          valuta: valuta
        });
      }
    }
  }

  onPopupHiding() {
    this.isVisible = false;
    this.visibleChange.emit(false);
  }

  onCancel() {
    this.isVisible = false;
    this.visibleChange.emit(false);
    this.smsPotraznjaForm.reset();
    // Reset form with default values
    this.initializeForm();
  }

  async onSubmit() {
    if (this.smsPotraznjaForm.valid) {
      this.loading = true;

      const formData = { ...this.smsPotraznjaForm.value };
      
      try {
        delete formData.drzava;
        const createdPotraznja = await this.apiClient.smsPotraznjePOST(formData);
        await this.apiClient.refreshCache();

        this.smsPotraznjaCreated.emit(createdPotraznja);
        this.isVisible = false;
        this.visibleChange.emit(false);
        this.smsPotraznjaForm.reset();
        // Reset form with default values
        this.initializeForm();

      } catch (error) {
        console.error('Error creating SMS potražnje:', error);
      } finally {
        this.loading = false;
      }
    } else {
      this.markFormGroupTouched(this.smsPotraznjaForm);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else {
        control?.markAsTouched();
      }
    });
  }
}